// Drawer (detail on the right) and Modal (centred dialog). Both trap focus, close on Escape and restore focus.
import { useRef, type ReactNode } from "react";
import { useFocusTrap } from "../lib/hooks";

function Overlay({ open, onClose, children, label, side }: { open: boolean; onClose: () => void; children: ReactNode; label: string; side: "right" | "center" }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open, onClose);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}
        className={side === "right"
          ? "absolute inset-y-0 right-0 flex w-full max-w-lg flex-col border-l border-line bg-paper shadow-pop"
          : "absolute left-1/2 top-[10vh] flex max-h-[80vh] w-[min(640px,calc(100vw-24px))] -translate-x-1/2 flex-col overflow-hidden rounded-lg border border-line bg-paper shadow-pop"}>
        {children}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <Overlay open={open} onClose={onClose} label={title} side="right">
      <header className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded px-2 py-1 text-muted hover:bg-sunken">✕</button>
      </header>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
      {footer && <footer className="border-t border-line px-4 py-3">{footer}</footer>}
    </Overlay>
  );
}

export function Modal({ open, onClose, label, children }: { open: boolean; onClose: () => void; label: string; children: ReactNode }) {
  return <Overlay open={open} onClose={onClose} label={label} side="center">{children}</Overlay>;
}
